import { useToast } from "@nabble/ui";
import { useCallback, useMemo } from "react";
import {
  type AssignableRole,
  fetchAssignableRoles,
  fetchOrganization,
  type ManagedOrganization,
  type OrganizationMember,
  removeMember,
  setMemberRoles,
} from "../../../api/organizations";
import { errorMessage } from "../../../errors";
import { useLoadedData } from "../../../hooks/useLoadedData";

export interface OrganizationPeople {
  organization: ManagedOrganization | undefined;
  /** Every role, and whether this organization's administrators may give it. */
  roles: AssignableRole[];
  loading: boolean;
  loadError: string | null;
  reload: () => void;
  /** Replaces the member's roles; resolves to whether it worked. */
  saveRoles: (member: OrganizationMember, roleIds: string[]) => Promise<boolean>;
  /** Removes the member; resolves to whether it worked. */
  remove: (member: OrganizationMember) => Promise<boolean>;
}

const NO_ROLES: AssignableRole[] = [];

/** The members of the managed organization, and the changes its administrators can make to them. */
export function useOrganizationPeople(organizationId: string | null): OrganizationPeople {
  const { show } = useToast();
  const load = useMemo(() => (organizationId ? () => fetchOrganization(organizationId) : null), [organizationId]);
  const loadRoles = useMemo(() => (organizationId ? () => fetchAssignableRoles(organizationId) : null), [organizationId]);
  const { data: organization, loading, loadError, reload } = useLoadedData(load, "We couldn't load the people of this organization.");
  const { data: roles } = useLoadedData(loadRoles, "We couldn't load the roles.");

  const saveRoles = useCallback(
    async (member: OrganizationMember, roleIds: string[]): Promise<boolean> => {
      if (!organizationId) return false;
      try {
        await setMemberRoles(organizationId, member.id, roleIds);
        show({ title: "Roles saved", text: `${member.displayName} now has the permissions of the roles you chose.`, tone: "success" });
        reload();
        return true;
      } catch (err) {
        show({ title: "We couldn't save the roles", text: errorMessage(err, "Please try again."), tone: "danger" });
        return false;
      }
    },
    [organizationId, reload, show],
  );

  async function remove(member: OrganizationMember): Promise<boolean> {
    if (!organizationId) return false;
    try {
      await removeMember(organizationId, member.id);
      show({ title: "Removed from the organization", text: `${member.displayName} no longer sees its work.`, tone: "success" });
      reload();
      return true;
    } catch (err) {
      show({ title: "We couldn't remove them", text: errorMessage(err, "Please try again."), tone: "danger" });
      return false;
    }
  }

  return { organization, roles: roles ?? NO_ROLES, loading, loadError, reload, saveRoles, remove };
}
